<script setup lang="ts">
import { computed } from 'vue';

type Variant = 'high' | 'medium' | 'low';

const props = withDefaults(
  defineProps<{
    size?: number;
    total: number;
    count: number;
    ringThickness?: number;
    variant?: Variant;
  }>(),
  {
    size: 100,
    ringThickness: 8,
    variant: 'high',
  }
);

const VARIANT_CLASSES = {
  high: {
    ring: 'tw:text-vuln-high',
    background: 'tw:text-vuln-high-light',
  },
  medium: {
    ring: 'tw:text-vuln-medium',
    background: 'tw:text-vuln-medium-light',
  },
  low: {
    ring: 'tw:text-vuln-low',
    background: 'tw:text-vuln-low-light',
  },
};

const classes = computed(() => VARIANT_CLASSES[props.variant]);
const progress = computed(() => (props.total > 0 ? (props.count / props.total) * 100 : 0));
const radius = computed(() => (100 - props.ringThickness) / 2);
const circumference = computed(() => +(2 * Math.PI * radius.value).toFixed(1));
const strokeDashOffset = computed(
  () => circumference.value - (circumference.value * progress.value) / 100
);
</script>

<template>
  <div class="tw:relative" :style="{ width: `${size}px`, height: `${size}px` }">
    <svg class="tw:size-full" viewBox="0 0 100 100">
      <circle
        class="tw:stroke-current"
        :class="classes.background"
        :stroke-width="ringThickness"
        cx="50"
        cy="50"
        :r="radius"
        fill="transparent"
      />
      <circle
        class="tw:stroke-current tw:-rotate-90 tw:origin-center tw:transition-all tw:duration-500"
        :class="classes.ring"
        :stroke-width="ringThickness"
        stroke-linecap="round"
        cx="50"
        cy="50"
        :r="radius"
        fill="transparent"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="strokeDashOffset"
      />
    </svg>
    <div
      class="tw:absolute tw:inset-0 tw:flex tw:flex-col tw:items-center tw:justify-center tw:-space-y-1"
    >
      <span class="tw:typography-h2">
        {{ count }}
      </span>
    </div>
  </div>
</template>
